"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Button } from "@/components/ui/button";
import { Clock, Layers, ListChecks, Target } from "lucide-react";
import { formatDuration, formatPrice } from "@/lib/exam/exam-detail-format";
import type { ExamData } from "@/types/exam-detail";

/**
 * Kartu CTA + sticky bar mobile — VERSI PUBLIK (milik zona landing,
 * pemisahan zona 2026-09-26): semantik tamu — gratis → "Mulai Ujian Gratis"
 * (workspace akan me-gate ke /masuk); berbayar → "Beli Paket" (checkout
 * me-gate anon). Versi user (sadis kepemilikan) hidup di
 * `(user)/ujianku/[slug]/_components/`.
 */
export function ExamDetailCta({ exam }: { exam: ExamData }) {
  const canStart = exam.type === "free";
  const totalQuestions = exam.sections.reduce((s, sec) => s + sec.questionCount, 0);
  const totalDuration = exam.sections.reduce((s, sec) => s + sec.durationMinutes, 0);
  const effectiveDuration =
    exam.durationMode === "global" ? exam.durationMinutes : totalDuration;
  const hasDiscount = exam.discountPrice && exam.discountPrice < exam.price;
  const displayPrice = hasDiscount ? exam.discountPrice! : exam.price;

  return (
    <div className="border-border bg-card overflow-hidden rounded-2xl border">
      {/* Spesifikasi — mosaik 2×2 ber-divider DI ATAS HARGA + ikon mikro. */}
      <div className="bg-muted/50 border-border/60 grid grid-cols-2 border-b">
        {[
          { value: formatDuration(effectiveDuration), label: "Durasi", Icon: Clock },
          { value: String(totalQuestions), label: "Soal", Icon: ListChecks },
          { value: String(exam.sections.length), label: "Seksi", Icon: Layers },
          {
            value:
              exam.passingGradeMode === "global"
                ? String(exam.passingGrade)
                : "Per seksi",
            label: "Passing",
            Icon: Target,
          },
        ].map(({ value, label, Icon }, i) => (
          <div
            key={label}
            className={`px-4 py-3 sm:px-5 ${i % 2 === 0 ? "border-border/60 border-r" : ""} ${i >= 2 ? "border-border/60 border-t" : ""}`}
          >
            <div className="text-foreground/40 text-3xs flex items-center gap-1 font-bold tracking-wide uppercase">
              <Icon size={12} className="text-primary/70 shrink-0" aria-hidden="true" />
              {label}
            </div>
            <p className="text-foreground mt-1 text-base leading-tight font-black">
              {value}
            </p>
          </div>
        ))}
      </div>
      <div className="p-5">
        {exam.type === "free" ? (
          <p className="text-primary text-2xl font-black">Gratis</p>
        ) : (
          <div>
            <div className="flex items-center justify-between gap-2">
              <span className="text-foreground/40 text-3xs font-bold tracking-wide uppercase">
                Harga
              </span>
              {hasDiscount && (
                <span className="bg-secondary text-foreground text-3xs rounded-full px-2 py-0.5 font-extrabold">
                  Hemat {Math.round(((exam.price - displayPrice) / exam.price) * 100)}%
                </span>
              )}
            </div>
            <div className="mt-1 flex flex-wrap items-baseline gap-x-2">
              <span className="text-foreground text-3xl leading-none font-black tracking-tight">
                {formatPrice(displayPrice)}
              </span>
              {hasDiscount && (
                <span className="text-foreground/35 text-sm font-medium line-through">
                  {formatPrice(exam.price)}
                </span>
              )}
            </div>
            <p className="text-foreground/50 mt-1.5 text-xs font-medium">
              Sekali bayar · akses selamanya
            </p>
          </div>
        )}

        <div className="mt-4">
          {canStart ? (
            <Button
              size="2xl"
              variant="default"
              className="w-full"
              render={<Link href={`/ujianku/${exam.slug}/mulai`} />}
            >
              Mulai Ujian Gratis
            </Button>
          ) : (
            <Button
              size="2xl"
              variant="default"
              className="w-full"
              render={<Link href={`/checkout/${exam.slug}`} />}
            >
              Beli Paket
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}

/**
 * Sticky purchase bar mobile (publik) — muncul saat kartu CTA utama terlewat
 * scroll. Desktop (lg) tidak dirender.
 */
export function ExamDetailMobileBar({ exam }: { exam: ExamData }) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const target = document.querySelector("[data-exam-cta]");
    if (!target) return;
    const io = new IntersectionObserver(
      (entries) => setVisible(!entries[0]?.isIntersecting),
      { rootMargin: "-80px 0px 0px 0px" },
    );
    io.observe(target);
    return () => io.disconnect();
  }, []);

  const canStart = exam.type === "free";
  const hasDiscount = exam.discountPrice && exam.discountPrice < exam.price;
  const displayPrice = hasDiscount ? exam.discountPrice! : exam.price;

  return (
    <div
      className={`border-border/80 bg-card/95 fixed inset-x-0 bottom-0 z-40 border-t px-4 py-3 backdrop-blur-md transition-transform duration-300 lg:hidden ${
        visible ? "translate-y-0" : "translate-y-full"
      }`}
    >
      <div className="mx-auto flex max-w-3xl items-center gap-3">
        <div className="min-w-0 flex-1">
          {exam.type === "free" ? (
            <p className="text-primary text-base font-black">Gratis</p>
          ) : (
            <div className="flex items-baseline gap-1.5">
              <span className="text-foreground text-base font-black">
                {formatPrice(displayPrice)}
              </span>
              {hasDiscount && (
                <span className="text-foreground/40 text-xs line-through">
                  {formatPrice(exam.price)}
                </span>
              )}
            </div>
          )}
          <p className="text-foreground/45 text-2xs truncate font-medium">{exam.title}</p>
        </div>
        <Button
          size="lg"
          variant={canStart ? "default" : "secondary"}
          className="rounded-full"
          render={
            <Link
              href={canStart ? `/ujianku/${exam.slug}/mulai` : `/checkout/${exam.slug}`}
            />
          }
        >
          {canStart ? "Mulai" : "Beli Paket"}
        </Button>
      </div>
    </div>
  );
}
